import React, { useState, useEffect } from 'react';
import { X, Calculator, MapPin, Users, Info } from 'lucide-react';
import { Button } from './ui/button';
import usePublicPackages from '../hooks/usePublicPackages';

/**
 * Professional B2B Billing Projection Modal
 * 
 * Phase 2B.2: Unterstützt jetzt beide Billing-Modelle.
 * - isLegacy=true: Bestandstarif-Berechnung (19,99€ + 11,99€/Standort)
 * - isLegacy=false: V2-Berechnung basierend auf Package-Daten aus Billing Catalog API
 */
export const BillingProjectionModal = ({ 
  isOpen, 
  onClose, 
  subscription,
  currentLocations = 1,
  currentEmployees = 0,
  isLegacy = true,           // Phase 2B.2: Billing-Modell vom Parent übergeben
  packageSlug = 'starter',   // Phase 2B.2: Für V2 Preisberechnung
}) => {
  const [locations, setLocations] = useState(currentLocations);
  const [employees, setEmployees] = useState(currentEmployees);
  const [billingPhase, setBillingPhase] = useState('normal');
  
  // Billing Catalog – Preise aus MongoDB via API
  const { getPrice, getLimits, getAddons } = usePublicPackages();

  // Reset to current values when modal opens
  useEffect(() => {
    if (isOpen) {
      setLocations(currentLocations);
      setEmployees(currentEmployees);
      if (subscription?.status === 'trialing') {
        setBillingPhase('trial');
      } else {
        setBillingPhase('normal');
      }
    }
  }, [isOpen, currentLocations, currentEmployees, subscription]);

  const VAT_RATE = 0.19;

  const calculateProjection = () => {
    try {
      if (isLegacy) {
        // ── LEGACY: Bestandstarif-Berechnung ──────────────────────────────
        const locationCost = locations === 1
          ? 19.99
          : 19.99 + ((locations - 1) * 11.99);
        const freeLimit = locations * 10;
        const extraEmployees = Math.max(0, employees - freeLimit);
        const employeeCost = extraEmployees * 0.99;
        const subtotalNetto = locationCost + employeeCost;
        const discount = billingPhase === 'trial' ? subtotalNetto : 0;
        const discountPercent = billingPhase === 'trial' ? 100 : 0;
        const totalNetto = Math.max(0, subtotalNetto - discount);
        const vat = totalNetto * VAT_RATE;
        return {
          locations, employees, freeLimit, extraEmployees,
          locationCost, employeeCost, subtotalNetto,
          discount, discountPercent, totalNetto,
          vat, totalBrutto: totalNetto + vat,
          subtotal: subtotalNetto, total: totalNetto + vat,
          _model: 'legacy',
        };
      } else {
        // ── BILLING V2: Paket-basierte Berechnung ─────────────────────────
        const pkgPrice = getPrice(packageSlug) ?? 29.90;
        const pkgLimits = getLimits(packageSlug);
        const empPerLoc = pkgLimits.employees_per_location ?? 15;
        const extraLocCents = pkgLimits.extra_location_monthly_cents ?? 1495;
        const extraLocPrice = extraLocCents / 100;

        const locationCost = locations === 1
          ? pkgPrice
          : pkgPrice + ((locations - 1) * extraLocPrice);
        const freeLimit = locations * empPerLoc;
        const extraEmployees = Math.max(0, employees - freeLimit);
        // V2: Add-ons sind Pakete (kein per-MA-Preis), Näherung für Vorschau
        const employeeCost = 0; // Add-ons sind keine laufenden MA-Kosten
        const subtotalNetto = locationCost + employeeCost;
        const discount = billingPhase === 'trial' ? subtotalNetto : 0;
        const discountPercent = billingPhase === 'trial' ? 100 : 0;
        const totalNetto = Math.max(0, subtotalNetto - discount);
        const vat = totalNetto * VAT_RATE;
        return {
          locations, employees, freeLimit, extraEmployees,
          locationCost, employeeCost: 0, subtotalNetto,
          discount, discountPercent, totalNetto,
          vat, totalBrutto: totalNetto + vat,
          subtotal: subtotalNetto, total: totalNetto + vat,
          _model: 'billing_v2',
          _extraLocPrice: extraLocPrice,
          _empPerLoc: empPerLoc,
        };
      }
    } catch (error) {
      console.error('Error calculating projection:', error);
      return null;
    }
  };

  const projection = calculateProjection();

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-50 animate-in fade-in duration-200"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div 
          className="bg-card rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto pointer-events-auto animate-in zoom-in-95 duration-200"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Calculator className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-foreground">
                  Abrechnungsvorschau
                </h2>
                <p className="text-sm text-muted-foreground">
                  Unverbindliche Kostenschätzung für Ihr Wachstum
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-muted-foreground" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Current Configuration Info */}
            <div className="bg-muted/50 rounded-lg p-4 flex items-start gap-3">
              <Info className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
              <div className="text-sm text-muted-foreground">
                <p className="font-medium text-foreground mb-1">Aktuelle Konfiguration</p>
                <p>
                  {currentLocations} {currentLocations === 1 ? 'Standort' : 'Standorte'} • {' '}
                  {currentEmployees} Mitarbeiter
                </p>
              </div>
            </div>

            {/* Input Section */}
            <div className="space-y-5">
              {/* Locations Input */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-foreground mb-3">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  Anzahl Standorte
                </label>
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={locations}
                  onChange={(e) => setLocations(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-full px-4 py-3 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent text-lg font-semibold"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  {isLegacy
                    ? 'Bestandstarif: 1. Standort 19,99 € · Weitere je 11,99 € (Netto)'
                    : `Billing V2: Starter ${getPrice(packageSlug) ?? '29,90'} € · Weitere Standorte je ${((getLimits(packageSlug).extra_location_monthly_cents ?? 1495) / 100).toFixed(2)} € (Netto)`
                  }
                </p>
              </div>

              {/* Employees Input */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-foreground mb-3">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  Anzahl Mitarbeiter (gesamt)
                </label>
                <input
                  type="number"
                  min="0"
                  max="500"
                  value={employees}
                  onChange={(e) => setEmployees(Math.max(0, parseInt(e.target.value) || 0))}
                  className="w-full px-4 py-3 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent text-lg font-semibold"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  {isLegacy
                    ? `Bestandstarif: ${locations * 10} Mitarbeiter inklusive · Zusätzlich: 0,99€/MA`
                    : `Billing V2: ${locations * (getLimits(packageSlug).employees_per_location ?? 15)} Mitarbeiter inklusive · Add-ons buchbar`
                  }
                </p>
              </div>

              {/* Phase indicator – nur anzeigen wenn Testphase aktiv */}
              {billingPhase === 'trial' && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                  <p className="text-xs text-blue-800 dark:text-blue-200">
                    <strong>Testphase aktiv:</strong> Dieser Monat wird Ihnen noch nicht berechnet.
                  </p>
                </div>
              )}
            </div>

            {/* Results Section */}
            {projection && (
              <div className="bg-muted/30 rounded-lg border-2 border-border p-5 space-y-3">
                <div className="flex items-center justify-between pb-3 border-b border-border">
                  <span className="text-sm font-medium text-muted-foreground">Kostenaufstellung</span>
                  <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">alle Preise zzgl. 19% MwSt.</span>
                </div>

                {/* Line Items (Netto) */}
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">
                      {projection.locations} {projection.locations === 1 ? 'Standort' : 'Standorte'} (Netto)
                    </span>
                    <span className="font-medium text-foreground">
                      {projection.locationCost.toFixed(2)} €
                    </span>
                  </div>

                  {projection.extraEmployees > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">
                        {projection.extraEmployees} zusätzliche Mitarbeiter (Netto)
                      </span>
                      <span className="font-medium text-foreground">
                        {projection.employeeCost.toFixed(2)} €
                      </span>
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-2 border-t border-border">
                    <span className="text-muted-foreground">Netto-Summe</span>
                    <span className="font-medium text-foreground">
                      {projection.subtotalNetto.toFixed(2)} €
                    </span>
                  </div>

                  {projection.discount > 0 && (
                    <div className="flex items-center justify-between text-green-600 dark:text-green-400">
                      <span className="font-medium">Rabatt ({projection.discountPercent}%)</span>
                      <span className="font-semibold">-{projection.discount.toFixed(2)} €</span>
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Netto nach Rabatt</span>
                    <span className="font-medium">{projection.totalNetto.toFixed(2)} €</span>
                  </div>

                  <div className="flex items-center justify-between text-gray-500">
                    <span>zzgl. 19% MwSt.</span>
                    <span>+ {projection.vat.toFixed(2)} €</span>
                  </div>
                </div>

                {/* Total (Brutto) */}
                <div className="flex items-center justify-between pt-3 border-t-2 border-border">
                  <span className="text-base font-semibold text-foreground">
                    Gesamt (Brutto) / Monat
                  </span>
                  <span className="text-2xl font-bold text-primary">
                    {projection.totalBrutto.toFixed(2)} €
                  </span>
                </div>
              </div>
            )}

            {/* Disclaimer */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <p className="text-xs text-blue-900 dark:text-blue-100 leading-relaxed">
                <strong>Hinweis:</strong> Dies ist eine unverbindliche Kostenschätzung. 
                Die tatsächliche Abrechnung erfolgt auf Basis Ihrer realen Konfiguration zum Abrechnungszeitpunkt.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-card border-t border-border p-6 flex items-center justify-end gap-3">
            <Button
              variant="outline"
              onClick={onClose}
            >
              Schließen
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};
